/**
 * Per-assignee workload table with inline mini-bars showing relative load.
 * Columns: Name | Open tasks (bar) | Est. h | Actual h | Overdue
 */
import type { WorkloadItem } from "../../lib/api";
import { formatHours } from "./chartHelpers";

interface Props {
  items: WorkloadItem[];
}

const MAX_BAR_W = 64; // px in SVG units — fits in a table cell

function MiniBar({ value, max, color }: { value: number; max: number; color: string }): preact.JSX.Element {
  const w = max > 0 ? Math.round((value / max) * MAX_BAR_W) : 0;
  return (
    <svg viewBox={`0 0 ${MAX_BAR_W} 10`} style={{ width: MAX_BAR_W, height: 10 }} aria-hidden="true">
      <rect x={0} y={1} width={MAX_BAR_W} height={8} rx={2} fill="currentColor" opacity={0.08} />
      {w > 0 && (
        <rect x={0} y={1} width={w} height={8} rx={2} fill={color} opacity={0.75} />
      )}
    </svg>
  );
}

export function WorkloadTable({ items }: Props): preact.JSX.Element {
  if (items.length === 0) {
    return (
      <p class="text-label italic text-[color:var(--color-on-surface-variant)]">
        Keine offenen Aufgaben mit Zuweisungen
      </p>
    );
  }

  const maxTasks = Math.max(1, ...items.map((it) => it.open_task_count));
  const maxEst = Math.max(1, ...items.map((it) => it.estimated_hours));

  return (
    <div class="overflow-x-auto">
      <table class="w-full text-micro border-collapse">
        <thead>
          <tr class="text-left text-[color:var(--color-on-surface-variant)] border-b border-[color:var(--color-outline-variant)]">
            <th class="py-1 pr-2 font-medium">Nutzer</th>
            <th class="py-1 pr-2 font-medium">Offen</th>
            <th class="py-1 pr-2 font-medium">Est.</th>
            <th class="py-1 pr-2 font-medium">Ist</th>
            <th class="py-1 font-medium">Überfällig</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr
              key={item.user_id}
              class="border-b border-[color:var(--color-outline-variant)] last:border-0 hover:bg-[color:var(--color-surface-container-low)]"
            >
              <td class="py-1.5 pr-2 font-medium text-[color:var(--color-on-surface)] max-w-[120px] truncate">
                {item.user_name}
              </td>
              <td class="py-1.5 pr-3">
                <div class="flex items-center gap-1.5">
                  <span class="font-medium text-[color:var(--color-on-surface)]">
                    {item.open_task_count}
                  </span>
                  <MiniBar
                    value={item.open_task_count}
                    max={maxTasks}
                    color="var(--color-primary)"
                  />
                </div>
              </td>
              <td class="py-1.5 pr-3">
                <div class="flex items-center gap-1.5">
                  <span>{formatHours(item.estimated_hours)}</span>
                  <MiniBar
                    value={item.estimated_hours}
                    max={maxEst}
                    color="var(--color-secondary)"
                  />
                </div>
              </td>
              <td class="py-1.5 pr-3 text-[color:var(--color-on-surface-variant)]">
                {formatHours(item.actual_hours)}
              </td>
              <td class="py-1.5">
                {item.overdue_count > 0 ? (
                  <span class="inline-flex items-center justify-center rounded-full bg-[color:var(--color-error-container,#ffdad6)] text-[color:var(--color-on-error-container,#93000a)] text-label font-semibold min-w-[18px] h-[18px] px-1">
                    {item.overdue_count}
                  </span>
                ) : (
                  <span class="text-[color:var(--color-on-surface-variant)] opacity-40">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
